import { unparse } from 'papaparse';

import { getComparator } from './sortingUtils';
import { secondsToMinutes } from './timeConversion';
import { type SessionSummary, type StudentMetricsRow } from '../components/dashboard/types';
import { type View } from '../context/DashboardContext';
import { type SchoolSettings } from '../types/apiTypes';

const prepareDataForCsv = (
    view: View,
    data: { studentMetricsRows: StudentMetricsRow[]; sessionSummary: SessionSummary },
    schoolSettings: SchoolSettings,
    t
) => {
    const { studentMetricsRows, sessionSummary } = data;

    switch (view) {
        case 'StudentDetail': {
            if (schoolSettings.has_lose_focus_tracking) {
                const translatedHeaderLabels = [
                    t('generic:Name'),
                    t('generic:Role'),
                    t('generic:Email'),
                    t('generic:ExternalId'),
                    t('talk-time-minutes'),
                    t('focus-time-minutes'),
                    t('hand-raises'),
                    t('feedback'),
                    t('stars')
                ];
                const translatedHeaderRow = `${translatedHeaderLabels.join(',')}\n`;

                const preparedData = [...studentMetricsRows].sort(getComparator('asc', 'sortName')).map((student) => ({
                    name: student.name,
                    role: t(
                        `generic:${student.role
                            .toLowerCase()
                            .replace(/\b[a-z]/g, (firstLetter) => firstLetter.toUpperCase())}`
                    ),
                    email: student.email,
                    externalId: student.externalId,
                    talkTime: secondsToMinutes(student.talkTime),
                    focusTime: secondsToMinutes(student.focusTime),
                    handRaises: student.handRaisedCount,
                    feedback: student.feedbackCount,
                    stars: student.starsMax
                }));

                const parsedData = unparse(preparedData, {
                    header: false,
                    columns: [
                        'name',
                        'role',
                        'email',
                        'externalId',
                        'talkTime',
                        'focusTime',
                        'handRaises',
                        'feedback',
                        'stars'
                    ]
                });

                return `${translatedHeaderRow}${parsedData}`;
            }
            // Do not include `focus time` if `has_lose_focus_tracking` is disabled
            const translatedHeaderRow = `${t('generic:Student')}, ${t('talk-time-minutes')}, ${t('hand-raises')}, ${t(
                'feedback'
            )}, ${t('stars')}\n`;

            const preparedData = [...studentMetricsRows].sort(getComparator('asc', 'sortName')).map((student) => ({
                name: student.name,
                talkTime: secondsToMinutes(student.talkTime),
                handRaises: student.handRaisedCount,
                feedback: student.feedbackCount,
                stars: student.starsMax
            }));

            const parsedData = unparse(preparedData, {
                header: false,
                columns: ['name', 'talkTime', 'handRaises', 'feedback', 'stars']
            });

            return `${translatedHeaderRow}${parsedData}`;
        }
        case 'SessionSummary': {
            const translatedHeaderLabels = [
                t('session-time-minutes'),
                t('teacher-talk-time-minutes'),
                t('student-talk-time-minutes'),
                t('attendees'),
                t('average-students-talk-time-minutes'),
                t('times-students-raised-hands'),
                t('times-students-gave-feedback')
            ];
            const translatedHeaderRow = `${translatedHeaderLabels.join(',')}\n`;
            const parsedData = [
                secondsToMinutes(sessionSummary.classTime),
                secondsToMinutes(sessionSummary.talkTime.teacher),
                secondsToMinutes(sessionSummary.talkTime.student),
                sessionSummary.attendance.student,
                secondsToMinutes(sessionSummary.averageTalkTime.student),
                sessionSummary.raisedHandCount.student,
                sessionSummary.feedbackCount.student
            ];

            return `${translatedHeaderRow}${parsedData}`;
        }
        default:
            break;
    }

    return '';
};

export { prepareDataForCsv };
